/**
 * Which rows of a standard or rideshare invoice (billed through armoves) go
 * where. A `move` row is a move; every other row is billed beside the moves:
 * products, one-time and manual charges, whatever their type says.
 *
 * A prepaid package is money in and a card fee is paid by its payment, so v1
 * kept both out of the subtotal and the CSVs leave them out. The page still
 * shows the prepaid row in its products table, as it always has.
 */
const MOVE_ROW_TYPE = 'move';
const PREPAID_ROW_TYPE = 'prepaid';
const FEE_ROW_TYPE = 'fee';

/** The row types the products table showed before it listed every non-move row. */
const PRODUCT_TABLE_ROW_TYPES: ReadonlySet<string> = new Set(['product', 'one-time', PREPAID_ROW_TYPE]);

type TypedRow = { type?: string | null };

export const isLegacyMoveRow = (armove: TypedRow): boolean => armove.type === MOVE_ROW_TYPE;

/** A billed row with no move: listed after the moves in the CSVs. A row with no type counts, so it isn't lost. */
export const isOtherLegacyArmove = (armove: TypedRow): boolean =>
  !isLegacyMoveRow(armove) && armove.type !== PREPAID_ROW_TYPE && armove.type !== FEE_ROW_TYPE;

/** The rows the page's products table lists: every billed non-move row, plus the prepaid package. */
export const isLegacyProductTableRow = (armove: TypedRow): boolean =>
  isOtherLegacyArmove(armove) || armove.type === PREPAID_ROW_TYPE;

/** True when the table holds a row that isn't a product, one-time charge or prepaid package, so it is titled for both. */
export const hasOtherLegacyCharges = (armoves: readonly TypedRow[] = []): boolean =>
  armoves.some(armove => !PRODUCT_TABLE_ROW_TYPES.has(armove.type ?? ''));

/** What a non-move row is called: its first detail's name, else its notes, else its type. */
export const describeLegacyRowName = (armove: TypedRow & { notes?: string | null; details?: ReadonlyArray<{ name?: string | null } | null> | null }): string => {
  const name = armove.details?.[0]?.name || armove.notes;
  if (name) return name;
  return armove.type ? armove.type.charAt(0).toUpperCase() + armove.type.slice(1) : 'Charge';
};
